import { HttpClient } from '@euler/lib/request';
import {
  ApdmAudienceResult,
  ApdmCampaignAudienceRequest,
  ApdmCampaignDetailResult,
  ApdmCampaignLeadsResult,
  ApdmCampaignListRequest,
  ApdmCampaignListResult,
  ApdmCampaignPreviewRequest,
  ApdmCampaignPreviewResult,
  ApdmCampaignPreviewSnapshot,
  ApdmCampaignPreviewSnapshotRequest,
  ApdmCampaignRecipientRecoveryRequest,
  ApdmCampaignRecipientsRequest,
  ApdmCampaignRecipientsResult,
  ApdmCampaignRefRequest,
  ApdmCampaignResumeAllRequest,
  ApdmCampaignStateResult,
  ApdmCampaignVersionRef,
  ApdmCampaignView,
  ApdmCampaignWizardOptionsRequest,
  ApdmCampaignWizardOptionsResult,
  ApdmCampaignWizardStep,
  ApdmCampaignWizardStepInput,
  ApdmClearRecipientContactInput,
  ApdmCouponCampaignSummary,
  ApdmCouponChoicesRequest,
  ApdmCouponChoicesResult,
  ApdmCreateCampaignInput,
  ApdmDashboardPeriod,
  ApdmDashboardResult,
  ApdmMarkRecipientArrivedInput,
  ApdmReachabilityActionResult,
  ApdmReachabilityListInput,
  ApdmReachabilityListResult,
  ApdmRecipientContactResult,
  ApdmRecipientConversionResult,
  ApdmRecipientReservationInput,
  ApdmRecipientReservationResult,
  ApdmSetCampaignCouponInput,
  ApdmUpdateCampaignDraftInput,
} from '@euler/model/apdm';
import {
  ApdmBuildCardKind,
  ApdmBuildCardResult,
  ApdmPlanAudienceListPage,
  ApdmPlanLaunchInput,
  ApdmPlanLaunchResult,
  ApdmSessionChecklistResult,
} from '@euler/model/apdm/build-plan';
import {
  ApdmCampaignReport,
  ApdmRecipientReportContext,
  ApdmRecipientReportRequest,
  ApdmReportRequest,
} from '@euler/model/apdm/reporting';

export class ApdmCampaignService {
  constructor(private readonly api: HttpClient) {}
  async recipientContext(
    input: ApdmRecipientReportRequest,
  ): Promise<ApdmRecipientReportContext> {
    return await this.api
      .get()
      .url(
        '/apdm/campaigns/:campaignId/recipients/:recipientId/report-context',
        { campaignId: input.campaignId, recipientId: input.recipientId },
      )
      .future();
  }
  async report(input: ApdmReportRequest): Promise<ApdmCampaignReport> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/report', {
        campaignId: input.campaignId,
      })
      .future();
  }
  async markArrived(
    input: ApdmMarkRecipientArrivedInput,
  ): Promise<ApdmRecipientConversionResult> {
    return await this.recipientAction('post', 'arrival', input, {
      expectedVersion: input.expectedVersion,
      orderNo: input.orderNo,
    });
  }
  async clearContact(
    input: ApdmClearRecipientContactInput,
  ): Promise<ApdmRecipientContactResult> {
    return await this.recipientAction('delete', 'do-not-contact', input, {
      expectedVersion: input.expectedVersion,
      expectedContactFingerprint: input.expectedContactFingerprint,
    });
  }
  async reserve(
    input: ApdmRecipientReservationInput,
  ): Promise<ApdmRecipientReservationResult> {
    return await this.recipientAction('post', 'reservations', input, {
      expectedVersion: input.expectedVersion,
      expectedArrivalAt: input.expectedArrivalAt,
      serviceAgentId: input.serviceAgentId,
      tags: input.tags,
      items: input.items,
      remark: input.remark,
    });
  }

  async create(input: ApdmCreateCampaignInput): Promise<ApdmCampaignView> {
    const {
      kind,
      title,
      mode,
      audience,
      content,
      couponCampaignId,
      posterTemplateAttachmentId,
      reviewEnabled,
      schedule,
      policy,
      variants,
    } = input;
    return await this.api
      .post()
      .url('/apdm/campaigns')
      .data({
        kind,
        title,
        mode,
        audience,
        content,
        couponCampaignId,
        posterTemplateAttachmentId,
        reviewEnabled,
        schedule,
        policy,
        variants,
      })
      .future();
  }
  async list(input: ApdmCampaignListRequest): Promise<ApdmCampaignListResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns', {
        afterCampaignId: input.afterCampaignId ?? undefined,
        limit: input.limit,
        status: input.status ?? undefined,
      })
      .future();
  }
  async detail(
    input: ApdmCampaignRefRequest,
  ): Promise<ApdmCampaignDetailResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId', { campaignId: input.campaignId })
      .future();
  }
  async updateDraft(
    input: ApdmUpdateCampaignDraftInput,
  ): Promise<ApdmCampaignView> {
    return await this.api
      .put()
      .url('/apdm/campaigns/:campaignId/draft', {
        campaignId: input.campaignId,
      })
      .data({ expectedVersion: input.expectedVersion, draft: input.draft })
      .future();
  }
  async confirm(input: ApdmCampaignVersionRef): Promise<ApdmCampaignView> {
    return await this.campaignAction('confirm', input);
  }
  async saveWizardStep(
    input: ApdmCampaignWizardStepInput,
  ): Promise<ApdmCampaignView> {
    switch (input.step) {
      case ApdmCampaignWizardStep.Basics:
        return await this.wizardStep(input, {
          title: input.data.title,
          kind: input.data.kind,
          mode: input.data.mode,
        });
      case ApdmCampaignWizardStep.Audience:
        return await this.wizardStep(input, {
          rule: input.data.rule,
          sources: input.data.sources,
        });
      case ApdmCampaignWizardStep.Content:
        return await this.wizardStep(input, {
          content: input.data.content,
          couponCampaignId: input.data.couponCampaignId,
          posterTemplateAttachmentId: input.data.posterTemplateAttachmentId,
          variants: input.data.variants,
        });
      case ApdmCampaignWizardStep.Schedule:
        return await this.wizardStep(input, {
          schedule: input.data.schedule,
          policy: input.data.policy,
          reviewEnabled: input.data.reviewEnabled,
        });
    }
    throw new Error('Invalid campaign wizard step');
  }
  async start(input: ApdmCampaignVersionRef): Promise<ApdmCampaignStateResult> {
    return await this.campaignAction('start', input);
  }
  async pause(input: ApdmCampaignVersionRef): Promise<ApdmCampaignStateResult> {
    return await this.campaignAction('pause', input);
  }
  async resume(
    input: ApdmCampaignVersionRef,
  ): Promise<ApdmCampaignStateResult> {
    return await this.campaignAction('resume', input);
  }
  async end(input: ApdmCampaignVersionRef): Promise<ApdmCampaignStateResult> {
    return await this.campaignAction('end', input);
  }
  async audiencePreview(
    input: ApdmCampaignAudienceRequest,
  ): Promise<ApdmAudienceResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/audience', {
        campaignId: input.campaignId,
        expectedVersion: input.expectedVersion,
        mode: input.mode,
        ...(input.mode === 'count'
          ? {}
          : { afterPlate: input.afterPlate ?? undefined, limit: input.limit }),
      })
      .future();
  }
  /** A step card a build reply kept, read back by the reply's id. */
  async planCard(input: {
    messageId: string;
    card: ApdmBuildCardKind;
  }): Promise<ApdmBuildCardResult> {
    return await this.api
      .get()
      .url('/apdm/plans/cards', {
        messageId: input.messageId,
        card: input.card,
      })
      .future();
  }
  /** Every customer an audience card counted, a page at a time. */
  async planAudience(input: {
    messageId: string;
    afterPlate: string | null;
  }): Promise<ApdmPlanAudienceListPage> {
    return await this.api
      .get()
      .url('/apdm/plans/audience', {
        messageId: input.messageId,
        ...(input.afterPlate ? { afterPlate: input.afterPlate } : {}),
      })
      .future();
  }
  /** The conversation's open build, for the progress strip. */
  async planChecklist(sessionId: string): Promise<ApdmSessionChecklistResult> {
    return await this.api
      .get()
      .url('/apdm/plans/checklist', { sessionId })
      .future();
  }
  /** Launches the plan the tapped campaign card showed; once per build. */
  async launchPlan(input: ApdmPlanLaunchInput): Promise<ApdmPlanLaunchResult> {
    return await this.api
      .post()
      .url('/apdm/plans/launch')
      .data({
        messageId: input.messageId,
        firstSend: input.firstSend,
        dailyWindow: input.dailyWindow,
      })
      .future();
  }
  /** The preview a chat reply showed, as it was saved. */
  async previewSnapshot(
    input: ApdmCampaignPreviewSnapshotRequest,
  ): Promise<ApdmCampaignPreviewSnapshot> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/preview/snapshot', {
        campaignId: input.campaignId,
        expectedVersion: input.expectedVersion,
        messageId: input.messageId,
      })
      .future();
  }
  async preview(
    input: ApdmCampaignPreviewRequest,
  ): Promise<ApdmCampaignPreviewResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/preview', {
        campaignId: input.campaignId,
        expectedVersion: input.expectedVersion,
      })
      .future();
  }
  async couponChoices(
    input: ApdmCouponChoicesRequest,
  ): Promise<ApdmCouponChoicesResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/coupon-choices', {
        campaignId: input.campaignId,
        afterCouponCampaignId: input.afterCouponCampaignId ?? undefined,
        limit: input.limit,
      })
      .future();
  }
  async coupon(input: {
    campaignId: string;
  }): Promise<ApdmCouponCampaignSummary | null> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/coupon', {
        campaignId: input.campaignId,
      })
      .future();
  }
  async setCoupon(
    input: ApdmSetCampaignCouponInput,
  ): Promise<ApdmCampaignView> {
    const { campaignId, ...body } = input;
    return await this.api
      .put()
      .url('/apdm/campaigns/:campaignId/coupon', { campaignId })
      .data(body)
      .future();
  }
  async options(
    input: ApdmCampaignWizardOptionsRequest,
  ): Promise<ApdmCampaignWizardOptionsResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns/wizard-options', {
        afterCouponCampaignId: input.afterCouponCampaignId ?? undefined,
        limit: input.limit,
      })
      .future();
  }
  async recipients(
    input: ApdmCampaignRecipientsRequest,
  ): Promise<ApdmCampaignRecipientsResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/recipients', {
        campaignId: input.campaignId,
        expectedVersion: input.expectedVersion,
        afterRecipientId: input.afterRecipientId ?? undefined,
        limit: input.limit,
        outreachState: input.outreachState ?? undefined,
        reachability: input.reachability ?? undefined,
        search: input.search ?? undefined,
        searchBy: input.searchBy ?? undefined,
      })
      .future();
  }
  async dashboard(period: ApdmDashboardPeriod): Promise<ApdmDashboardResult> {
    return await this.api.get().url('/apdm/dashboard', { period }).future();
  }
  async leads(campaignId: string): Promise<ApdmCampaignLeadsResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/leads', { campaignId })
      .future();
  }
  async reachability(
    input: ApdmReachabilityListInput,
  ): Promise<ApdmReachabilityListResult> {
    return await this.api
      .get()
      .url('/apdm/campaigns/:campaignId/reachability', {
        campaignId: input.campaignId,
        expectedVersion: input.expectedVersion,
        afterRecipientId: input.afterRecipientId ?? undefined,
        limit: input.limit,
      })
      .future();
  }
  async resumeRecipient(
    input: ApdmCampaignRecipientRecoveryRequest,
  ): Promise<ApdmReachabilityActionResult> {
    return await this.recipientAction('post', 'resume', input, {
      expectedVersion: input.expectedVersion,
      expectedAttempt: input.expectedAttempt,
      expectedSendSeq: input.expectedSendSeq,
    });
  }
  async resumeAll(
    input: ApdmCampaignResumeAllRequest,
  ): Promise<ApdmReachabilityActionResult> {
    return await this.api
      .post()
      .url('/apdm/campaigns/:campaignId/recipients/resume', {
        campaignId: input.campaignId,
      })
      .data({ expectedVersion: input.expectedVersion })
      .future();
  }
  async markSent(
    input: ApdmCampaignRecipientRecoveryRequest,
  ): Promise<ApdmReachabilityActionResult> {
    return await this.recipientAction('post', 'mark-sent', input, {
      expectedVersion: input.expectedVersion,
      expectedAttempt: input.expectedAttempt,
      expectedSendSeq: input.expectedSendSeq,
    });
  }
  private async campaignAction<Result>(
    action: 'confirm' | 'start' | 'pause' | 'resume' | 'end',
    input: ApdmCampaignVersionRef,
  ): Promise<Result> {
    return await this.api
      .post()
      .url('/apdm/campaigns/:campaignId/' + action, {
        campaignId: input.campaignId,
      })
      .data({ expectedVersion: input.expectedVersion })
      .future();
  }
  private async wizardStep(
    input: ApdmCampaignWizardStepInput,
    data: object,
  ): Promise<ApdmCampaignView> {
    return await this.api
      .put()
      .url('/apdm/campaigns/:campaignId/wizard-steps/:step', {
        campaignId: input.campaignId,
        step: input.step,
      })
      .data({ expectedVersion: input.expectedVersion, data })
      .future();
  }
  private async recipientAction<Result>(
    method: 'post' | 'delete',
    action: string,
    target: { campaignId: string; recipientId: string },
    body: object,
  ): Promise<Result> {
    return await this.api[method]()
      .url('/apdm/campaigns/:campaignId/recipients/:recipientId/' + action, {
        campaignId: target.campaignId,
        recipientId: target.recipientId,
      })
      .data(body)
      .future();
  }
}
